import { useCallback, useMemo, useState } from 'react';
import {
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridSortModel,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { BigQueryPage, MobileDevice, SyncData } from '../../../../types';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import dayjs from 'dayjs';
import { ModuleList } from '../../../shared/analytics/module-list';
import UserDeviceDialog from '../../user-management/user-details/devices/device-details-dialog';
import DeleteUserDeviceDialog from '../../user-management/user-details/devices/device-delete-dialog';
import ApproveUserDeviceDialog from '../../user-management/user-details/devices/device-approve-dialog';
import BlockUserDeviceDialog from '../../user-management/user-details/devices/device-block-dialog';
import WipeUserDeviceDialog from '../../user-management/user-details/devices/device-wipe-dialog';
import WipeAccountUserDeviceDialog from '../../user-management/user-details/devices/device-account-wipe-dialog';
import { bigQueryFilterOperators } from '../../../shared/big-query-filter-operators';
import MobileDevicesIndexedToolbar from './mobile-devices-indexed-toolbar';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import { getIndexedMobileDevices, UserKeys } from '../../../api/users-api';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Messages } from '../../../utils/message-constants';
import { isMissingBigQueryTableError } from '../../../utils/error-utils';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';

import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Android from '@mui/icons-material/Android';
import Apple from '@mui/icons-material/Apple';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import FolderDelete from '@mui/icons-material/FolderDelete';
import Google from '@mui/icons-material/Google';
import Pending from '@mui/icons-material/Pending';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
import Smartphone from '@mui/icons-material/Smartphone';

const transformFilterModelForApi = (
  model: GridFilterModel
): GridFilterModel => {
  const updateItem = (item: GridFilterItem): GridFilterItem | null => {
    if (!item.value) {
      return null;
    }
    if (
      (item.field === 'firstSync' || item.field === 'lastSync') &&
      item.value
    ) {
      if (item.operator === 'after' || item.operator === 'onOrBefore') {
        const date = new Date(item.value);
        date.setUTCHours(23, 59, 59, 999);
        return { ...item, value: date.getTime().toString() };
      }
      return { ...item, value: new Date(item.value).getTime().toString() };
    }
    if (item.field === 'model') {
      return { ...item, field: 'deviceName' };
    }
    return item;
  };

  const quickFilterValue = model.quickFilterValues?.[0];

  const processedItems = (model.items || [])
    .map(updateItem)
    .filter((item): item is GridFilterItem => item !== null);

  if (quickFilterValue && quickFilterValue.length > 0) {
    const nameFilterIndex = processedItems.findIndex(
      (item) => item.field === 'name'
    );
    if (nameFilterIndex > -1) {
      processedItems[nameFilterIndex].value = quickFilterValue;
    } else {
      processedItems.push({
        field: 'name',
        operator: 'contains',
        value: quickFilterValue,
      });
    }
  }

  return { ...model, items: processedItems };
};

interface MobileDevicesIndexedGridProps {
  gridKey?: string | null;
  userEmail?: string;
  userRoles: string[];
  toolsRoles: string[];
  changeLogRoles: string[];
  features: Record<string, boolean>;
}

export default function MobileDevicesIndexedGrid({
  userEmail,
  toolsRoles,
  changeLogRoles,
  features,
  gridKey,
}: MobileDevicesIndexedGridProps) {
  const { createEvent } = useAnalytics();
  const [openDeleteDevice, setOpenDeleteDevice] = useState(false);
  const [openBlockDevice, setOpenBlockDevice] = useState(false);
  const [openApproveDevice, setOpenApproveDevice] = useState(false);
  const [openWipeDevice, setOpenWipeDevice] = useState(false);
  const [openWipeAccountDevice, setOpenWipeAccountDevice] = useState(false);
  const [openViewDeviceDetails, setOpenViewDeviceDetails] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<MobileDevice>();

  const columns: GridColDef<MobileDevice>[] = useMemo(
    () => [
      {
        field: 'model',
        headerName: 'Device Name',
        flex: 2,
        pinnable: true,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value, row) => {
          return `${row.name[0].split(' ')[0]}'s ${value}`;
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        pinnable: false,
        filterable: false,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value) => {
          return value[0];
        },
      },
      {
        field: 'serialNumber',
        headerName: 'Serial Number',
        flex: 1,
        pinnable: false,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 2,
        pinnable: false,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value) => {
          return value[0];
        },
      },
      {
        field: 'os',
        headerName: 'OS',
        flex: 1,
        pinnable: false,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 20,
        pinnable: false,
        filterable: true,
        display: 'flex',
        type: 'singleSelect',
        filterOperators: getGridSingleSelectOperators()
          .filter((op) => op.value === 'is' || op.value === 'not')
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueOptions: [
          { label: 'iOS', value: 'IOS_SYNC' },
          { label: 'Android', value: 'ANDROID' },
          { label: 'Google', value: 'GOOGLE_SYNC' },
        ],
        renderCell: (params) => {
          if (params.value === 'IOS_SYNC') {
            return (
              <Tooltip title={params.value}>
                <Apple
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#7D7D7D', width: 'fit-content' }}
                />
              </Tooltip>
            );
          } else if (params.value === 'ANDROID') {
            return (
              <Tooltip title={params.value}>
                <Android
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#A4C639' }}
                />
              </Tooltip>
            );
          } else if (params.value === 'GOOGLE_SYNC') {
            return (
              <Tooltip title={params.value}>
                <Google
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#4285F4' }}
                />
              </Tooltip>
            );
          } else {
            return (
              <Tooltip title={params.value}>
                <Smartphone
                  key={params.value}
                  fontSize="small"
                  sx={{ color: 'disabled' }}
                />
              </Tooltip>
            );
          }
        },
      },
      {
        field: 'isForCompanyUseOnly',
        headerName: 'Ownership',
        type: 'singleSelect',
        sortable: false,
        filterOperators: getGridSingleSelectOperators()
          .filter((op) => op.value === 'is' || op.value === 'not')
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueOptions: [
          { label: 'Company owned', value: 'Company Owned' },
          { label: 'User owned', value: 'User Owned' },
        ],
        width: 130,
        pinnable: false,
        filterable: true,
        valueGetter: (value: boolean) => {
          if (value) {
            return 'Company Owned';
          } else {
            return 'User Owned';
          }
        },
      },
      {
        field: 'firstSync',
        headerName: 'First Sync',
        type: 'date',
        width: 110,
        pinnable: false,
        filterable: true,
        filterOperators: getGridDateOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedDateOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value: SyncData) => {
          const date = new Date(value.value);
          return date;
        },
        valueFormatter: (value?: Date) => {
          const differenceInDays = dayjs().diff(dayjs(value), 'day');
          if (differenceInDays === 0) {
            const differenceInHours = dayjs().diff(dayjs(value), 'hour');
            if (differenceInHours === 0) {
              const differenceInMinutes = dayjs().diff(dayjs(value), 'minute');
              if (differenceInMinutes === 0) {
                return 'Just now';
              } else {
                return `${differenceInMinutes} minutes ago`;
              }
            } else {
              return `${differenceInHours} hours ago`;
            }
          } else {
            return `${differenceInDays} days ago`;
          }
        },
      },
      {
        field: 'lastSync',
        headerName: 'Last Sync',
        type: 'date',
        width: 110,
        pinnable: false,
        filterable: true,
        filterOperators: getGridDateOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedDateOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value: SyncData) => {
          const date = new Date(value.value);
          return date;
        },
        valueFormatter: (value?: Date) => {
          const differenceInDays = dayjs().diff(dayjs(value), 'day');
          if (differenceInDays === 0) {
            const differenceInHours = dayjs().diff(dayjs(value), 'hour');
            if (differenceInHours === 0) {
              const differenceInMinutes = dayjs().diff(dayjs(value), 'minute');
              if (differenceInMinutes === 0) {
                return 'Just now';
              } else {
                return `${differenceInMinutes} minutes ago`;
              }
            } else {
              return `${differenceInHours} hours ago`;
            }
          } else {
            return `${differenceInDays} days ago`;
          }
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        pinnable: false,
        filterable: true,
        type: 'singleSelect',
        filterOperators: getGridSingleSelectOperators()
          .filter((op) => op.value === 'is' || op.value === 'not')
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueOptions: [
          { label: 'approved', value: 'APPROVED' },
          { label: 'blocked', value: 'BLOCKED' },
          { label: 'accounted wiped', value: 'UNKNOWN' },
          { label: 'wiped', value: 'WIPED' },
          { label: 'pending', value: 'PENDING' },
        ],
        renderCell: (params) => {
          if (!params.value) {
            params.value = 'UNKNOWN';
          }

          if (params.value === 'APPROVED') {
            return (
              <Chip
                variant="outlined"
                icon={<Check />}
                label="approved"
                color="success"
                size="small"
              />
            );
          } else if (params.value === 'BLOCKED') {
            return (
              <Chip
                variant="outlined"
                icon={<DoNotDisturb />}
                label={'blocked'}
                color={'warning'}
                size="small"
              />
            );
          } else if (params.value === 'UNKNOWN') {
            return (
              <Chip
                variant="outlined"
                icon={<FolderDelete />}
                label={'account wiped'}
                color={'error'}
                size="small"
              />
            );
          } else if (params.value === 'WIPED') {
            return (
              <Chip
                variant="outlined"
                icon={<DeleteSweep />}
                label={'wiped'}
                size="small"
                color="error"
              />
            );
          } else if (params.value === 'PENDING') {
            return (
              <Chip
                variant="outlined"
                icon={<Pending />}
                label={'pending'}
                size="small"
                color="warning"
              />
            );
          } else {
            return (
              <Chip
                variant="outlined"
                icon={<Close />}
                label={params.value.toLowerCase()}
                size="small"
                color="info"
              />
            );
          }
        },
      },
    ],
    []
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'mobileManagementIndexedGridStates'
  );

  const defaultGridState: GridInitialState = {
    columns: {
      columnVisibilityModel: {
        serialNumber: false,
      },
    },
  };

  const defaultFilterModel = {
    items: [],
    logicOperator: GridLogicOperator.And,
    quickFilterLogicOperator: GridLogicOperator.And,
    quickFilterValues: [],
  };

  const initialGridFilterModel =
    initialState?.filter?.filterModel || defaultFilterModel;

  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([
    {
      field: initialState?.sorting?.sortModel?.[0]?.field || 'name',
      sort: initialState?.sorting?.sortModel?.[0]?.sort || 'asc',
    },
  ]);

  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>(
    initialGridFilterModel
  );

  const onSortChange = useCallback(
    (gridSortModel: GridSortModel) => {
      const updateItems = (item: any) => {
        if (item.field === 'model') {
          return { ...item, field: 'deviceName' };
        }
        return item;
      };

      const updatedItems = gridSortModel.map((item) => {
        return updateItems(item);
      });

      if (updatedItems.length > 0) {
        setGridSortModel(gridSortModel);
        setSortModel([...updatedItems]);
      } else {
        setGridSortModel(gridSortModel);
        setSortModel([{ field: 'name', sort: 'asc' }]);
      }
    },
    [gridSortModel]
  );

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 50,
    page: 0,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(() =>
    transformFilterModelForApi(initialGridFilterModel)
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);

  const onFilterChange = useCallback(
    (newGridFilterModel: GridFilterModel) => {
      setGridFilterModel(newGridFilterModel);

      const apiFilterModel = transformFilterModelForApi(newGridFilterModel);

      if (JSON.stringify(apiFilterModel) !== JSON.stringify(filterModel)) {
        setFilterModel(apiFilterModel);
      }
      setPaginationModel((prev) => ({
        ...prev,
        page: 0,
      }));
    },
    [filterModel]
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      UserKeys.MobileDevicesIndexedPage,
      paginationModel.page,
      paginationModel.pageSize,
      filterModel,
      sortModel,
    ],
    queryFn: () => {
      const object: BigQueryPage<MobileDevice[]> = {
        results: [],
        pageSize: paginationModel.pageSize,
        currentPage: paginationModel.page,
        totalCount: 0,
      };
      return getIndexedMobileDevices(object, filterModel, sortModel);
    },
    enabled: isFeatureEnabled('indexMobileDevicesReact', features),
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('indexed mobile devices'),
    },
  });

  const isMissingTableError = error && isMissingBigQueryTableError(error);
  const overlays = isMissingTableError
    ? createGridOverlays(
        'Mobile Devices are currently being indexed',
        <PhoneIphoneRounded />,
        'The grid will refresh once complete.',
        'The grid will refresh once complete.',
        true
      )
    : createGridOverlays(
        'Mobile Devices',
        <PhoneIphoneRounded />,
        'Try changing/adding more to your search term.'
      );

  const selectedDevices =
    data?.results.filter(
      (device) => selectedDeviceIds?.includes(device.resourceId)
    ) || [];

  const toolbarProps = useMemo(
    () => ({
      selectedDevices,
      toolsRoles,
      setOpenDeleteDevice,
      setOpenApproveDevice,
      setOpenBlockDevice,
      setOpenWipeDevice,
      setOpenWipeAccountDevice,
    }),
    [selectedDevices, toolsRoles]
  );

  const CustomToolbar = useToolbar(MobileDevicesIndexedToolbar, toolbarProps);

  return (
    <>
      <SharedDataGridServer
        checkboxSelection
        selectedRows={selectedDeviceIds}
        setSelectedRows={setSelectedDeviceIds}
        getRowId={(row) => row.resourceId}
        initialState={initialState || defaultGridState}
        slots={{
          toolbar: CustomToolbar,
          ...overlays,
        }}
        filterOperator={[GridLogicOperator.And]}
        filterPanelSx={{
          minWidth: 800,
        }}
        disableRowSelectionOnClick
        data={data}
        onRowClick={(params) => {
          setSelectedDevice(params.row);
          setOpenViewDeviceDetails(true);
          createEvent(
            analytics.user_management.user_details.devices.viewed_mobile_device,
            {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.mobileDevices,
            }
          );
        }}
        columns={computedColumns}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (isLoading || isFetching) return;
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
            });
          } else {
            setPaginationModel({
              ...newModel,
            });
          }
        }}
        filterModel={gridFilterModel}
        onFilterModelChange={(newModel) => {
          if (isLoading || isFetching) return;
          onFilterChange(newModel);
        }}
        sortModel={gridSortModel}
        onSortModelChange={(newModel) => {
          if (isLoading || isFetching) return;
          onSortChange(newModel);
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={isLoading || isFetching}
        gridKey={gridKey}
      />

      {openViewDeviceDetails && selectedDevice ? (
        <UserDeviceDialog
          open={openViewDeviceDetails}
          handleClose={() => setOpenViewDeviceDetails(false)}
          selectedDevice={selectedDevice}
        />
      ) : null}
      {openDeleteDevice && selectedDevices.length ? (
        <DeleteUserDeviceDialog
          open={openDeleteDevice}
          handleClose={() => setOpenDeleteDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openApproveDevice && selectedDevices.length ? (
        <ApproveUserDeviceDialog
          open={openApproveDevice}
          handleClose={() => setOpenApproveDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openBlockDevice && selectedDevices.length ? (
        <BlockUserDeviceDialog
          open={openBlockDevice}
          handleClose={() => setOpenBlockDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openWipeDevice && selectedDevices.length ? (
        <WipeUserDeviceDialog
          open={openWipeDevice}
          handleClose={() => setOpenWipeDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openWipeAccountDevice && selectedDevices.length ? (
        <WipeAccountUserDeviceDialog
          open={openWipeAccountDevice}
          handleClose={() => setOpenWipeAccountDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
    </>
  );
}
